import { useState, useCallback } from 'react';
import axiosInstance from '../api/axios';
import { API_ENDPOINTS } from '../api/endpoints';

/**
 * Queue analytics dashboard — wraps GET /api/v1/queues/dashboard.
 * Response shape mirrors the mobile app's DashboardData (same endpoint), which
 * is the authoritative contract (resolves MODULE_API_PLAN.md O5).
 */

export type DashboardPeriod = 'today' | 'weekly' | 'monthly' | 'yearly' | 'custom';

export interface TokenCounts {
  booked: number;
  waiting: number;
  in_progress: number;
  completed: number;
  hold: number;
  skipped: number;
  cancelled: number;
  no_show: number;
}

export interface DashboardQueue {
  queue_id: string;
  queue_name: string;
  earnings: number;
  token_counts: TokenCounts;
}

export interface DashboardData {
  total_earnings: number;
  total_token_counts: TokenCounts;
  period_label: string;
  start_date: string;
  end_date: string;
  queues: DashboardQueue[];
}

interface GetDashboardResponse {
  success: boolean;
  message: string;
  data: DashboardData;
}

export const useQueueDashboard = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(
    async (period: DashboardPeriod, startDate?: string, endDate?: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ period });
        if (period === 'custom' && startDate && endDate) {
          params.set('start_date', startDate);
          params.set('end_date', endDate);
        }
        const res = await axiosInstance.get<GetDashboardResponse>(
          `${API_ENDPOINTS.GET_DASHBOARD}?${params.toString()}`
        );
        setData(res.data?.data ?? null);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load dashboard data.');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { data, isLoading, error, fetchDashboard };
};
